import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { VendorRolesService, VendorRoleWithStatuses, VendorStatusItem } from 'src/app/services/vendor-roles.service';

export interface VendorRoleDialogData {
  mode: 'create' | 'edit';
  orgId: number;
  role?: VendorRoleWithStatuses;
  allStatuses?: VendorStatusItem[];
}

@Component({
  standalone: true,
  selector: 'app-vendor-role-dialog',
  template: `
    <h2 mat-dialog-title>
      @if (data.mode === 'create') {
      {{ 'Add Vendor Role' | translate }}
      } @else {
      {{ 'Edit Vendor Role' | translate }}
      }
    </h2>
    <mat-dialog-content>
      <div class="flex flex-col gap-3 min-w-[380px]">
        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'Role Name' | translate }}</mat-label>
          <input matInput [(ngModel)]="roleName" required maxlength="100" />
        </mat-form-field>

        <mat-form-field appearance="fill" class="w-full">
          <mat-label>{{ 'Description' | translate }}</mat-label>
          <textarea matInput [(ngModel)]="roleDescription" rows="3"></textarea>
        </mat-form-field>

        <mat-checkbox [(ngModel)]="isActive">{{ 'Active' | translate }}</mat-checkbox>

        <!-- Visible Statuses -->
        @if (allStatuses.length > 0) {
        <div class="mt-1">
          <div class="font-semibold text-[13px] mb-2 text-gray-600">
            {{ 'Visible Statuses' | translate }}
          </div>
          <div class="flex flex-col gap-1.5">
            @for (status of allStatuses; track status.id) {
            <mat-checkbox
              [checked]="statusState[status.id] || false"
              (change)="statusState[status.id] = $event.checked"
            >
              {{ status.labelForStatus }}
            </mat-checkbox>
            }
          </div>
        </div>
        } @if (statusesLoading) {
        <div class="flex justify-center py-2">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
        } @if (errorMessage) {
        <div class="text-red-800 text-[13px]">{{ errorMessage }}</div>
        }
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>{{ 'Cancel' | translate }}</button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="saving || !roleName.trim()">
        <span class="flex flex-row items-center gap-2">
          @if (saving) {
          <mat-spinner diameter="18"></mat-spinner>
          } @if (data.mode === 'create') {
          {{ 'Add Role' | translate }}
          } @else {
          {{ 'Update Role' | translate }}
          }
        </span>
      </button>
    </mat-dialog-actions>
  `,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatIconModule,
    TranslateModule,
  ],
})
export class VendorRoleDialogComponent implements OnInit {
  roleName = '';
  roleDescription = '';
  isActive = true;
  saving = false;
  errorMessage = '';

  allStatuses: VendorStatusItem[] = [];
  statusState: Record<number, boolean> = {};
  statusesLoading = false;

  constructor(
    private dialogRef: MatDialogRef<VendorRoleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VendorRoleDialogData,
    private vendorRolesService: VendorRolesService,
  ) {
    if (data.mode === 'edit' && data.role) {
      this.roleName = data.role.role_name;
      this.roleDescription = data.role.role_description || '';
      this.isActive = data.role.is_active;
    }
  }

  ngOnInit(): void {
    this.loadStatuses();
  }

  private loadStatuses(): void {
    if (this.data.allStatuses && this.data.allStatuses.length > 0) {
      this.allStatuses = this.data.allStatuses;
      this.buildStatusState();
      return;
    }

    this.statusesLoading = true;
    this.vendorRolesService.getAllStatuses().subscribe({
      next: (statuses) => {
        this.allStatuses = statuses;
        this.buildStatusState();
        this.statusesLoading = false;
      },
      error: () => {
        this.statusesLoading = false;
      },
    });
  }

  private buildStatusState(): void {
    const assignedIds = new Set((this.data.role?.statuses ?? []).map((s) => s.id));
    for (const status of this.allStatuses) {
      this.statusState[status.id] = assignedIds.has(status.id);
    }
  }

  save(): void {
    if (!this.roleName.trim()) return;

    this.saving = true;
    this.errorMessage = '';

    if (this.data.mode === 'create') {
      this.vendorRolesService
        .createVendorRole({
          role_name: this.roleName.trim(),
          role_description: this.roleDescription.trim() || undefined,
          org_id: this.data.orgId,
          is_active: this.isActive,
        })
        .subscribe({
          next: (created) => {
            this.saveStatuses(created.id);
          },
          error: (err) => {
            this.saving = false;
            this.errorMessage = err?.error?.message || 'Error creating vendor role';
          },
        });
    } else if (this.data.mode === 'edit' && this.data.role) {
      this.vendorRolesService
        .updateVendorRole(this.data.role.id, this.data.orgId, {
          role_name: this.roleName.trim(),
          role_description: this.roleDescription.trim(),
          is_active: this.isActive,
        })
        .subscribe({
          next: () => {
            this.saveStatuses(this.data.role!.id);
          },
          error: (err) => {
            this.saving = false;
            this.errorMessage = err?.error?.message || 'Error updating vendor role';
          },
        });
    }
  }

  private saveStatuses(roleId: number): void {
    const statusIds = Object.entries(this.statusState)
      .filter(([, checked]) => checked)
      .map(([id]) => Number(id));

    this.vendorRolesService.updateRoleStatuses(roleId, statusIds).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: () => {
        // Role saved but statuses failed — still close since main save succeeded
        this.saving = false;
        this.dialogRef.close(true);
      },
    });
  }
}
